import { Modal, UI, Utils } from 'common';
import includes from 'lodash/includes';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import { CancelAccountContext } from './CancelAccount.context';

const { ModalContext } = Modal;
const { Session, Util } = Utils;
const { WrappedButton, WrappedButtonType, Spinner } = UI;

const ContinueToCancelButton = ({ onClick }) => (
	<WrappedButton
		className="text-only"
		label="Continue to cancel"
		buttonType={WrappedButtonType.Secondary}
		onClick={onClick}
		dataqa="cancel"
	/>
);

const CtaButton = ({ label, link, onClick }) => (
	<WrappedButton onClick={onClick} label={label} dataqa="cta" link={link} />
);

const CTA_TYPE = {
	HowTo: 'howTo',
	Waive: 'waive',
	LearningCenter: 'learningCenter',
};

const CancelQuestionAnswer = {
	// dropdown 1
	Expensive: 'expensive',
	NoTime: 'noTime',
	TechnicalIssues: 'technicalIssues',
	LacksFeatures: 'lacksFeatures',

	// dropdown 2
	RetailLocation: 'retailLocation',
	PostOffice: 'postOffice',
	Usps: 'usps',
	Marketplace: 'marketplace',
	OtherCarrier: 'otherCarrier',
	Postage: 'postage',
	Stamps: 'stamps',
	MultiCarrier: 'multiCarrier',
	Closed: 'closed',

	// Dropdown 3
	Shipstation: 'shipstation',
	Shipworks: 'shipworks',
	Amazon: 'amazon',
	EBay: 'eBay',
	Etsy: 'etsy',
	Marketplaces: 'marketplaces',

	Other: 'other',
};

class AdaptiveCTA extends Component {
	modalContext = null;

	cancelAccountContext = null;

	constructor() {
		super();

		this.state = {
			ctaType: '',
			showThankYou: false,
			isLoading: false,
		};
	}

	componentDidMount() {
		this.parseDropdowns();
	}

	skipCTA = () => {
		this.cancelAccountContext.setStep('Downgrade');
	};

	parseWaiveState = () => {
		const { IsServiceFeesWaivedBefore, LastBilledDate } =
			Session.getInternalAccountInfo();
		const isServiceFeesWaivedBefore =
			Util.getCookie('IsServiceFeesWaivedBefore') || IsServiceFeesWaivedBefore;
		const isInTrialPeriod = LastBilledDate === '0001-01-01T00:00:00'; // user in trial period if there's no billing date info

		// per WEBAPPS-24904, the following criteria must be met to display the 1 month waiver text:
		// (1) user has never accepted a service fee waive
		// (2) user is currently in their trial period
		if (!isServiceFeesWaivedBefore && isInTrialPeriod) {
			this.setState({
				ctaType: CTA_TYPE.Waive,
			});
		} else {
			this.skipCTA();
		}
	};

	waiveServiceFees = async () => {
		// per WEBAPPS-27242, display Spinner when user accepts waiver
		this.setState({
			isLoading: true,
		});

		// per WEBAPPS-24904, need to waive 2 months
		const response = await AccountCommunication.waiveServiceFees({
			months: 2,
		});

		this.setState({
			isLoading: false,
		});

		if (AccountCommunication.isSuccessful(response)) {
			// this is being set for a fast refresh of the page where InternalAccountInformation doesn't reflect the IsServiceFeesWaivedBefore value change
			Util.setCookie('IsServiceFeesWaivedBefore', true, '1 minute');

			this.setState({ showThankYou: true, ctaType: '' });
		} else {
			this.modalContext.showError({ response });
		}
	};

	// Per Product spec:
	// https://stamps.atlassian.net/wiki/spaces/PRODREQ/pages/3123412/Online+Cancellation+Redesign
	parseDropdowns = () => {
		const context = this.cancelAccountContext;

		if (context.Dropdown1Answer === CancelQuestionAnswer.TechnicalIssues) {
			// show 'Learning Center' content
			this.setState({
				ctaType: CTA_TYPE.LearningCenter,
			});
		} else if (this.shouldShowHowTo()) {
			// show 'How-To' content
			this.setState({
				ctaType: CTA_TYPE.HowTo,
			});
		} else if (this.shouldShowWaive()) {
			this.parseWaiveState();
		} else {
			// show 'Downgrade' content
			this.skipCTA();
		}
	};

	/**
	 * Return `true` to show 'How-To' content if conditions are met:
	 * 1. dropdown 1 option is either 'too expensive' or 'didn't have time, and
	 * 2. dropdown 2 option is 'multi-carrier', and
	 * 3. dropdown 3 option is either 'ShipStation' or 'Shipworks'.
	 * @returns {boolean}
	 */
	shouldShowHowTo = () => {
		const context = this.cancelAccountContext;

		// select either 'too expensive' or 'didn't have time' option
		const isDropdown1ConditionMet =
			context.Dropdown1Answer === CancelQuestionAnswer.Expensive ||
			context.Dropdown1Answer === CancelQuestionAnswer.NoTime;

		// select 'multi-carrier' option
		const isDropdown2ConditionMet =
			context.Dropdown2Answer === CancelQuestionAnswer.MultiCarrier;

		// select 'ShipStation' or 'Shipworks' option
		const isDropdown3ConditionMet =
			context.Dropdown3Answer === CancelQuestionAnswer.Shipstation ||
			context.Dropdown3Answer === CancelQuestionAnswer.Shipworks;

		return (
			isDropdown1ConditionMet === true &&
			isDropdown2ConditionMet === true &&
			isDropdown3ConditionMet === true
		);
	};

	/**
	 * Return `true` to show 'Waive' content if conditions are met.
	 * @returns {boolean}
	 */
	shouldShowWaive = () => {
		const context = this.cancelAccountContext;

		// select 'didn't have time' option in dropdown 1
		if (context.Dropdown1Answer === CancelQuestionAnswer.NoTime) {
			// select 'multi-carrier' option in dropdown 2
			if (context.Dropdown2Answer === CancelQuestionAnswer.MultiCarrier) {
				// select 'other' option in dropdown 3
				return context.Dropdown3Answer === CancelQuestionAnswer.Other;
			}
			// select 'marketplace' option in dropdown 2
			else if (context.Dropdown2Answer === CancelQuestionAnswer.Marketplace) {
				// select one of the following options in dropdown 3
				return includes(
					[
						CancelQuestionAnswer.Amazon,
						CancelQuestionAnswer.EBay,
						CancelQuestionAnswer.Etsy,
						CancelQuestionAnswer.Marketplaces,
						CancelQuestionAnswer.Other,
					],
					context.Dropdown3Answer
				);
			}

			// or select either one of the following options in dropdown 2
			return includes(
				[
					CancelQuestionAnswer.RetailLocation,
					CancelQuestionAnswer.PostOffice,
					CancelQuestionAnswer.Usps,
					CancelQuestionAnswer.OtherCarrier,
					CancelQuestionAnswer.Postage,
					CancelQuestionAnswer.Other,
				],
				context.Dropdown2Answer
			);
		}

		return false;
	};

	openCtaLink = e => {
		const link = e.currentTarget.getAttribute('link');
		window.open(link, '_blank');
	};

	HowTo = () => {
		const type =
			this.cancelAccountContext.Dropdown3Answer === 'shipstation'
				? 'ShipStation'
				: 'ShipWorks';
		const link =
			type === 'ShipStation'
				? 'https://help.shipstation.com/hc/en-us/articles/360026142111-Stamps-com'
				: 'https://support.shipworks.com/hc/en-us/articles/360022654891-Adding-a-Stamps-com-Account-in-ShipWorks';

		return (
			<div className="cta-type how-to">
				<h1 className="primary-header">{`Using ${type}? Great news.`}</h1>
				<p>
					{`Your Stamps.com account is free of charge when you connect it to your ${type} account. `}
					Click{' '}
					<a href={link} target="_blank" rel="noopener noreferrer">
						here
					</a>{' '}
					{`to learn how to connect your free Stamps.com account to ${type}.`}
				</p>
				<UI.ActionRow>
					<CtaButton label="Learn How" onClick={this.openCtaLink} link={link} />
					<ContinueToCancelButton onClick={this.skipCTA} />
				</UI.ActionRow>
			</div>
		);
	};

	Waive = () => {
		const benefits = [
			'Print Stamps',
			'Print Shipping Labels',
			'Print postage right on envelopes',
			'Access all mail classes',
			'Receive discounts you can’t get at the Post Office',
		];

		return (
			<div className="cta-type waive">
				<h1 className="primary-header">{`Didn’t have time to try Stamps.com? No problem.`}</h1>
				<p>{`We'll waive your service fees for 1 month so you can experience all the benefits of Stamps.com with no risk.`}</p>
				<ul>
					{benefits.map(benefit => (
						<li key={benefit}>{benefit}</li>
					))}
				</ul>
				<UI.ActionRow>
					<CtaButton
						label="Waive 1 month of service fee"
						onClick={this.waiveServiceFees}
					/>
					<ContinueToCancelButton onClick={this.skipCTA} />
				</UI.ActionRow>
			</div>
		);
	};

	LearningCenter = () => {
		const contactNumber = Util.getFraudNumber();

		return (
			<div className="cta-type learning-center">
				<h1 className="primary-header">
					We’re sorry for any technical difficulties you may have experienced.
				</h1>
				<p>
					{`We’re here to help in any way we can. Please give us a call at ${contactNumber}
					Monday-Friday 8am to 8pm Central Time or visit our Learning Center.`}
				</p>
				<UI.ActionRow>
					<CtaButton
						label="Learning Center"
						onClick={this.openCtaLink}
						link={'http://www.stamps.com/help'}
					/>
					<ContinueToCancelButton onClick={this.skipCTA} />
				</UI.ActionRow>
			</div>
		);
	};

	ThankYou = () => {
		return (
			<div className="cta-type thank-you">
				<h1 className="primary-header">Thank you.</h1>
				<p data-qa="cta-thanks-desc" {...{ dataqa: 'cta-thanks-desc' }}>
					Your Stamps.com services fees will be waived for 30 days so you can
					have more time to explore all the time-saving features of Stamps.com.
				</p>
			</div>
		);
	};

	render() {
		const { ctaType, showThankYou, isLoading } = this.state;

		return (
			<ModalContext.Consumer>
				{context1 => {
					this.modalContext = context1;
					return (
						<CancelAccountContext.Consumer>
							{context2 => {
								this.cancelAccountContext = context2;
								return (
									<div className="cancel-account-adaptive-cta">
										{ctaType === CTA_TYPE.HowTo && <this.HowTo />}
										{ctaType === CTA_TYPE.Waive && <this.Waive />}
										{ctaType === CTA_TYPE.LearningCenter && (
											<this.LearningCenter />
										)}
										{showThankYou && <this.ThankYou />}
										{isLoading && <Spinner msg="Applying plan changes..." />}
									</div>
								);
							}}
						</CancelAccountContext.Consumer>
					);
				}}
			</ModalContext.Consumer>
		);
	}
}

export default AdaptiveCTA;
